import React, { useMemo, useRef, useState } from "react";
import { useAppSelector } from "../store/hooks";
import {
  selectIsSocketConnected,
  selectIsSocketConnecting,
  selectSocketError,
} from "../store/socketSlice";
import "./AppStatusTray.css";

interface AppStatusTrayProps {
  soundEnabled: boolean;
  onToggleSound: () => void;
}

type ConnectionState = "connected" | "connecting" | "error" | "idle";

const CONNECTION_LABEL: Record<ConnectionState, string> = {
  connected: "В сети",
  connecting: "Подключение…",
  error: "Нет соединения",
  idle: "Не подключено",
};

/**
 * Единый «системный лоток» в правом нижнем углу:
 *
 *  - Индикатор соединения с сервером (точка + контекстный лейбл).
 *  - Переключатель звука уведомлений.
 *
 * Сам лоток монтируется как часть NotificationCenter — это гарантирует,
 * что тосты стекаются НАД лотком и не наезжают на его кнопки.
 */
const AppStatusTray: React.FC<AppStatusTrayProps> = ({
  soundEnabled,
  onToggleSound,
}) => {
  const isConnected = useAppSelector(selectIsSocketConnected);
  const isConnecting = useAppSelector(selectIsSocketConnecting);
  const error = useAppSelector(selectSocketError);

  // Если сокет ни разу не появлялся — индикатор не показываем (мы на лендинге
  // или странице авторизации). Sound toggle при этом всё равно живёт.
  const everConnectedRef = useRef(false);
  if (isConnected || isConnecting || error) everConnectedRef.current = true;

  const connectionState: ConnectionState = useMemo(() => {
    if (error) return "error";
    if (isConnecting) return "connecting";
    if (isConnected) return "connected";
    return "idle";
  }, [error, isConnecting, isConnected]);

  const showConnection =
    everConnectedRef.current && connectionState !== "idle";

  const [errorOpen, setErrorOpen] = useState(false);

  const connectionLabel = CONNECTION_LABEL[connectionState];
  const showLabelInline =
    showConnection &&
    (connectionState === "connecting" || connectionState === "error");

  return (
    <div className="app-status-tray" role="status" aria-live="polite">
      {showConnection && (
        <button
          type="button"
          className={`app-status-tray__item app-status-tray__connection app-status-tray__connection--${connectionState}`}
          title={error || connectionLabel}
          onClick={() => {
            if (connectionState === "error") setErrorOpen((v) => !v);
          }}
          aria-label={connectionLabel}
        >
          <span className="app-status-tray__dot" aria-hidden="true" />
          {showLabelInline && (
            <span className="app-status-tray__label">{connectionLabel}</span>
          )}
        </button>
      )}

      {showConnection && (
        <span className="app-status-tray__divider" aria-hidden="true" />
      )}

      <button
        type="button"
        className={`app-status-tray__item app-status-tray__sound ${
          soundEnabled ? "is-on" : "is-off"
        }`}
        onClick={onToggleSound}
        title={
          soundEnabled
            ? "Звук уведомлений включён — отключить"
            : "Звук уведомлений выключен — включить"
        }
        aria-label={
          soundEnabled
            ? "Отключить звук уведомлений"
            : "Включить звук уведомлений"
        }
        aria-pressed={soundEnabled}
      >
        {soundEnabled ? (
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
          </svg>
        ) : (
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
          </svg>
        )}
      </button>

      {errorOpen && error && (
        <div
          className="app-status-tray__error-popover"
          role="alert"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="app-status-tray__error-title">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            Нет соединения
          </div>
          <div className="app-status-tray__error-body">{error}</div>
          <button
            type="button"
            className="app-status-tray__error-close"
            onClick={() => setErrorOpen(false)}
            aria-label="Закрыть"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default AppStatusTray;
